import * as React from 'react';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import PricePerformanceChart from './PricePerformanceChart';

interface Props {
  ticker: string;
}

function PricePerformancePage({ ticker }: Props) {
  const normalized = (ticker || '').toUpperCase();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">
              <a href={`/ticker/${encodeURIComponent(normalized)}`}>{normalized}</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">Price performance</li>
          </ol>
        </nav>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <div>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {normalized} Price Performance
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Explore how {normalized} has performed across different timeframes. Pick a timeframe below and hover the chart to see the percentage return from that point to today.
          </Typography>
        </div>
      </Stack>

      <PricePerformanceChart ticker={normalized} variant="full" />

      <Box sx={{ mt: 3 }}>
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">How to read this chart</h5>
            <ul style={{ marginBottom: 0 }}>
              <li>
                <strong>Timeframes</strong> pick the window you care about, from the past day to the stock's full history.
              </li>
              <li>
                <strong>Hover tooltip</strong> shows the closing price on that date and the percentage return from that date to today, so you can see what a purchase on that date would be worth now.
              </li>
              <li>
                <strong>Include dividends</strong> switches the chart to total return (price gains plus reinvested dividends), which is a fuller picture of long-term performance.
              </li>
              <li>
                Historical prices are automatically adjusted for stock splits, so a past stock split does not show up as a fake price drop.
              </li>
            </ul>
          </div>
        </div>
      </Box>
    </Container>
  );
}

export default PricePerformancePage;
